import { useState, useRef, useEffect } from 'react';
import { useGmbAlertNotifications } from '../hooks/useGmbAlertNotifications';

const SEVERITY_CONFIG: Record<string, { icon: string; color: string; bg: string }> = {
  critical: { icon: 'ri-error-warning-line', color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-900/20' },
  warning: { icon: 'ri-alert-line', color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/20' },
  informational: { icon: 'ri-information-line', color: 'text-sky-500', bg: 'bg-sky-50 dark:bg-sky-900/20' },
};

function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function GmbNotificationBell({ onNavigate }: { onNavigate?: (section: string) => void }) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const { unreadCount, alerts, markAllRead, isMarkingAllRead } = useGmbAlertNotifications();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const recentAlerts = alerts
    .filter((a: any) => !a.is_read && !a.is_acknowledged)
    .slice(0, 5);

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        title="Notifications"
      >
        <i className="ri-notification-line text-gray-600 dark:text-gray-300 text-xl"></i>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">GMB Alerts</h3>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">Important updates for your GBP</p>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead()}
                disabled={isMarkingAllRead}
                className="text-[11px] text-violet-600 dark:text-violet-400 hover:underline disabled:opacity-50"
              >
                {isMarkingAllRead ? 'Marking...' : 'Mark all read'}
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {recentAlerts.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <i className="ri-notification-off-line text-gray-300 dark:text-gray-600 text-3xl mb-2 block"></i>
                <p className="text-xs text-gray-500 dark:text-gray-400">No alerts right now</p>
              </div>
            ) : (
              recentAlerts.map((alert: any) => {
                const sev = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.informational;
                return (
                  <div
                    key={alert.id}
                    onClick={() => { setOpen(false); onNavigate?.('alerts-permissions'); }}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer ${!alert.is_read ? 'bg-violet-50/50 dark:bg-violet-900/10' : ''}`}
                  >
                    <div className={`mt-0.5 p-1.5 rounded-lg ${sev.bg}`}>
                      <i className={`${sev.icon} ${sev.color} text-sm`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{alert.title}</p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5">{alert.message}</p>
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">{timeAgo(alert.created_at)}</p>
                    </div>
                    {!alert.is_read && (
                      <span className="mt-1 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0"></span>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {recentAlerts.length > 0 && (
            <div className="px-4 py-2.5 border-t border-gray-100 dark:border-gray-700">
              <button
                onClick={() => { setOpen(false); onNavigate?.('alerts-permissions'); }}
                className="text-[11px] font-medium text-violet-600 dark:text-violet-400 hover:underline"
              >
                View All Alerts →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
